import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function SearchSpacesModal({ isOpen, onClose, onJoinSpace }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [spaces, setSpaces] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadPublicSpaces()
    }
  }, [isOpen])

  useEffect(() => {
    if (searchQuery) {
      searchSpaces()
    } else {
      loadPublicSpaces()
    }
  }, [searchQuery])

  const loadPublicSpaces = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('spaces')
        .select('*, space_members(count)')
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      setSpaces(data || [])
    } catch (err) {
      console.error('Error loading public spaces:', err)
      setSpaces([])
    } finally {
      setLoading(false)
    }
  }

  const searchSpaces = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('spaces')
        .select('*, space_members(count)')
        .eq('is_public', true)
        .ilike('name', `%${searchQuery}%`)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      setSpaces(data || [])
    } catch (err) {
      console.error('Error searching spaces:', err)
      setSpaces([])
    } finally {
      setLoading(false)
    }
  }

  const handleJoinSpace = (space) => {
    onJoinSpace(space.invite_code)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      {/* Clickable backdrop to close modal */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-gjyl-slate-900/95 backdrop-blur-gjyl border-2 border-gjyl-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden animate-scale-in shadow-gjyl-lift-strong">
        {/* Header */}
        <div className="sticky top-0 bg-gjyl-slate-800/95 backdrop-blur-gjyl p-6 border-b border-gjyl-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">Discover Spaces</h2>
            <button
              onClick={onClose}
              className="btn-icon"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Search Input */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search public spaces..."
              className="w-full pl-10 pr-4 py-3 bg-gjyl-slate-800 text-white placeholder-slate-500 rounded-gjyl border border-gjyl-slate-700 focus:border-gjyl-indigo-500 focus:ring-2 focus:ring-gjyl-indigo-500/30 outline-none transition-all"
              autoFocus
            />
          </div>
        </div>

        {/* Spaces List */}
        <div className="p-4 overflow-y-auto max-h-[60vh] scrollbar-gjyl">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gjyl-indigo-500"></div>
            </div>
          ) : spaces.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">🔍</div>
              <p className="text-slate-400">No public spaces found</p>
              <p className="text-sm text-slate-500 mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {spaces.map((space) => (
                <div
                  key={space.id}
                  className="glass hover:glass-strong rounded-gjyl p-4 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    {/* Space Icon */}
                    <div className="w-16 h-16 rounded-gjyl bg-gjyl-slate-700 flex items-center justify-center font-bold text-xl flex-shrink-0 group-hover:bg-gjyl-indigo-500/20 transition-colors">
                      {space.icon_url ? (
                        <img src={space.icon_url} alt={space.name} className="w-full h-full rounded-gjyl object-cover" />
                      ) : (
                        space.name.substring(0, 2).toUpperCase()
                      )}
                    </div>

                    {/* Space Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white mb-1 truncate">
                        {space.name}
                      </h3>
                      {space.description && (
                        <p className="text-sm text-slate-400 mb-2 line-clamp-2">
                          {space.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          {space.members?.[0]?.count || 0} members
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          Public
                        </span>
                      </div>
                    </div>

                    {/* Join Button */}
                    <button
                      onClick={() => handleJoinSpace(space)}
                      className="px-4 py-2 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 text-white font-medium rounded-gjyl transition-all hover:scale-105 active:scale-95 flex-shrink-0"
                    >
                      Join
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gjyl-slate-800/95 backdrop-blur-gjyl p-4 border-t border-gjyl-slate-700">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{spaces.length} public space{spaces.length !== 1 ? 's' : ''} available</span>
            <button
              onClick={onClose}
              className="px-3 py-1.5 glass hover:glass-strong rounded text-white transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
